import { z } from 'zod';
import { canonicalJson } from './canonical-json.mjs';
import { parseCanonicalJson } from './parse-json.mjs';
import { APP_BRIDGE } from '../contracts/app-limits.mjs';

const id = z
  .string()
  .min(1)
  .max(200)
  .refine((value) => !/[\u0000-\u001f\u007f]/u.test(value));
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const integer = z
  .string()
  .regex(/^-?(0|[1-9][0-9]*)$/)
  .max(100);
const count = z
  .string()
  .regex(/^(0|[1-9][0-9]*)$/)
  .max(100);
const decimal = z
  .string()
  .regex(/^-?(0|[1-9][0-9]*)(\.[0-9]+)?$/)
  .max(200);
const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const parsed = new Date(value + 'T00:00:00Z');
    return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  });
const instant = z
  .string()
  .datetime({ offset: false })
  .refine((value) => Number.isFinite(Date.parse(value)));
const timezone = z
  .string()
  .min(1)
  .max(100)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: value });
      return true;
    } catch {
      return false;
    }
  });
const ids = z.array(id).max(1000);
const schemaVersion = z.literal('data.v2');

function unique(values, ctx, path) {
  if (new Set(values).size !== values.length)
    ctx.addIssue({ code: 'custom', path, message: 'Duplicate declared identity' });
}
const sourceFields = { id, digest, readTemplateRef: id.optional() };
const sourceBinding = z.discriminatedUnion('kind', [
  z
    .object({
      ...sourceFields,
      kind: z.literal('connector'),
      connectionMode: z.enum(['owner', 'viewer']),
      ownerConnectionRef: id.optional(),
      queryTemplateDigest: digest,
      companyPolicyVersion: id,
      disclosurePolicyRef: id,
    })
    .strict(),
  z.object({ ...sourceFields, kind: z.literal('collection') }).strict(),
  z.object({ ...sourceFields, kind: z.literal('derived') }).strict(),
]);
const column = z
  .object({
    id,
    label: z.string().max(2000),
    type: z.enum(['text', 'integer', 'decimal', 'boolean', 'date', 'timestamp', 'json', 'file']),
    nullable: z.boolean(),
    unit: z.string().max(100).optional(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    scale: z.number().int().min(0).max(100).optional(),
    timezone: timezone.optional(),
  })
  .strict();
const jsonValue = z.custom((value) => {
  if (value === null || value === undefined) return false;
  try {
    canonicalJson(value);
    return true;
  } catch {
    return false;
  }
});
const presentValue = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), value: z.string().max(65536) }).strict(),
  z.object({ type: z.literal('integer'), value: integer }).strict(),
  z.object({ type: z.literal('decimal'), value: decimal }).strict(),
  z.object({ type: z.literal('boolean'), value: z.boolean() }).strict(),
  z.object({ type: z.literal('date'), value: date }).strict(),
  z.object({ type: z.literal('timestamp'), value: instant }).strict(),
  z.object({ type: z.literal('json'), value: jsonValue }).strict(),
  z
    .object({ type: z.literal('file'), value: z.object({ fileId: id, sha256: digest }).strict() })
    .strict(),
]);
const cell = z.discriminatedUnion('status', [
  z.object({ status: z.literal('present'), value: presentValue, evidenceRefIds: ids }).strict(),
  z.object({ status: z.literal('null'), evidenceRefIds: ids }).strict(),
  z
    .object({
      status: z.literal('missing'),
      reason: z.enum(['not_observed', 'unavailable', 'not_applicable']),
      evidenceRefIds: ids,
    })
    .strict(),
]);
function checkCoverageCounts(value, ctx) {
  if (value.expectedRows === undefined) return;
  if (BigInt(value.observedRows) > BigInt(value.expectedRows))
    ctx.addIssue({ code: 'custom', message: 'Observed count exceeds expected count' });
  if (value.status === 'complete' && value.observedRows !== value.expectedRows)
    ctx.addIssue({
      code: 'custom',
      message: 'Complete coverage must account for the expected rows',
    });
}
const coverage = z
  .object({
    status: z.enum(['complete', 'partial', 'unavailable']),
    reason: z
      .enum(['source_limit', 'page_limit', 'revoked', 'provider_error', 'no_history'])
      .optional(),
    expectedRows: count.optional(),
    observedRows: count,
    coveredFrom: instant.nullable(),
    coveredThrough: instant.nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    checkCoverageCounts(value, ctx);
    if (value.status === 'complete' && value.reason !== undefined)
      ctx.addIssue({ code: 'custom', message: 'Limited coverage cannot be labelled complete' });
    if (
      value.coveredFrom &&
      value.coveredThrough &&
      Date.parse(value.coveredFrom) > Date.parse(value.coveredThrough)
    )
      ctx.addIssue({ code: 'custom', message: 'Reversed coverage interval' });
  });
const evidence = z
  .object({
    schemaVersion,
    id,
    sourceBindingId: id,
    sourceRecordRef: id.nullable(),
    sourceAsOf: instant.nullable(),
    fetchedAt: instant,
    digest,
    disclosure: z.enum(['authorized', 'redacted']),
    immutableEvidenceRef: id.nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.disclosure === 'redacted' && value.immutableEvidenceRef !== null)
      ctx.addIssue({
        code: 'custom',
        message: 'Redacted evidence cannot disclose a private handle',
      });
  });
const common = {
  schemaVersion,
  id,
  companyId: id,
  appId: id.nullable(),
  fetchedAt: instant,
  watermark: instant.nullable(),
  timezone,
};

function checkPresent(value, definition, ctx, path) {
  if (value.type !== definition.type)
    ctx.addIssue({ code: 'custom', path, message: 'Value does not match declared column' });
  if (
    value.type === 'decimal' &&
    definition.scale !== undefined &&
    value.value.split('.')[1]?.length > definition.scale
  )
    ctx.addIssue({ code: 'custom', path, message: 'Value exceeds declared scale' });
}
function rowColumns(values, columns, ctx, path) {
  const allowed = new Set(columns.map((definition) => definition.id));
  for (const key of Object.keys(values))
    if (!allowed.has(key))
      ctx.addIssue({ code: 'custom', path: [...path, key], message: 'Undeclared column' });
  for (const definition of columns) {
    const value = Object.hasOwn(values, definition.id) ? values[definition.id] : undefined;
    const at = [...path, definition.id];
    if (value === undefined) {
      ctx.addIssue({ code: 'custom', path: at, message: 'Explicit missingness required' });
      continue;
    }
    if (value.status === 'null' && !definition.nullable)
      ctx.addIssue({ code: 'custom', path: at, message: 'Column is not nullable' });
    if (value.status === 'present') checkPresent(value.value, definition, ctx, at);
  }
}
export const datasetSchema = z
  .object({
    ...common,
    datasetVersion: id,
    sourceBindings: z.array(sourceBinding).max(100),
    columns: z.array(column).max(1000),
    rows: z.array(z.object({ id, cells: z.record(z.string(), cell) }).strict()).max(10000),
    evidence: z.array(evidence).max(10000),
    page: z
      .object({
        nextCursor: z.string().min(1).max(4096).nullable(),
        hasMore: z.boolean(),
        readVersion: id,
        returnedRows: z.number().int().min(0).max(10000),
        coverage,
      })
      .strict(),
    authorizedDigest: digest,
  })
  .strict()
  .superRefine((value, ctx) => {
    unique(
      value.columns.map((v) => v.id),
      ctx,
      ['columns'],
    );
    unique(
      value.rows.map((v) => v.id),
      ctx,
      ['rows'],
    );
    unique(
      value.sourceBindings.map((v) => v.id),
      ctx,
      ['sourceBindings'],
    );
    unique(
      value.evidence.map((v) => v.id),
      ctx,
      ['evidence'],
    );
    const sources = new Set(value.sourceBindings.map((v) => v.id));
    for (const ref of value.evidence)
      if (!sources.has(ref.sourceBindingId))
        ctx.addIssue({ code: 'custom', message: 'Evidence source is undeclared' });
    checkDatasetRows(value, ctx);
    if (
      value.page.returnedRows !== value.rows.length ||
      value.page.hasMore !== (value.page.nextCursor !== null)
    )
      ctx.addIssue({ code: 'custom', path: ['page'], message: 'Page count/cursor mismatch' });
    if (value.page.hasMore && value.page.coverage.status === 'complete')
      ctx.addIssue({
        code: 'custom',
        path: ['page'],
        message: 'An unfinished page is not complete coverage',
      });
  });
function checkDatasetRows(value, ctx) {
  const evidenceIds = new Set(value.evidence.map((v) => v.id));
  for (const [index, row] of value.rows.entries()) {
    rowColumns(row.cells, value.columns, ctx, ['rows', index, 'cells']);
    cellReferences(row.cells, ctx, ['rows', index, 'cells'], evidenceIds);
  }
}
function cellReferences(values, ctx, path, evidenceIds) {
  for (const value of Object.values(values)) {
    unique(value.evidenceRefIds, ctx, path);
    for (const ref of value.evidenceRefIds)
      if (evidenceIds && !evidenceIds.has(ref))
        ctx.addIssue({ code: 'custom', message: 'Unknown evidence reference' });
  }
}
const eventCellsSchema = z
  .object({ columns: z.array(column).max(1000), cells: z.record(z.string(), cell) })
  .strict()
  .superRefine((value, ctx) => {
    unique(
      value.columns.map((v) => v.id),
      ctx,
      ['columns'],
    );
    rowColumns(value.cells, value.columns, ctx, ['cells']);
    cellReferences(value.cells, ctx, ['cells']);
  });
export const seriesSchema = z
  .object({
    ...common,
    seriesVersion: id,
    keyColumns: z.array(column).min(1).max(100),
    valueColumns: z.array(column).min(1).max(1000),
    sourceBinding,
    coverage,
    authorizedDigest: digest,
  })
  .strict()
  .superRefine((value, ctx) =>
    unique(
      [...value.keyColumns, ...value.valueColumns].map((v) => v.id),
      ctx,
      ['columns'],
    ),
  );
export const observationSchema = z
  .object({
    schemaVersion,
    id,
    seriesId: id,
    key: z.record(z.string(), presentValue),
    observedAt: instant,
    sourceAsOf: instant.nullable(),
    fetchedAt: instant,
    refreshId: id,
    backfilled: z.boolean(),
    values: z.record(z.string(), cell),
    evidenceRefIds: ids,
    digest,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.backfilled && value.sourceAsOf === null)
      ctx.addIssue({ code: 'custom', message: 'Backfill requires source-as-of evidence' });
    unique(value.evidenceRefIds, ctx, ['evidenceRefIds']);
  });
const dependency = z.object({ id, digest }).strict();
export const snapshotSchema = z
  .object({
    schemaVersion,
    id,
    companyId: id,
    artifactId: id,
    createdAt: instant,
    datasetRefs: z.array(dependency.extend({ version: id })).max(1000),
    observationRefs: z.array(dependency).max(10000),
    evidenceRefs: z.array(dependency).max(10000),
    coverage,
    dependencyDigest: digest,
    authorizedDigest: digest,
  })
  .strict()
  .superRefine((value, ctx) => {
    for (const key of ['datasetRefs', 'observationRefs', 'evidenceRefs'])
      unique(
        value[key].map((v) => v.id),
        ctx,
        [key],
      );
  });

function parse(schema, input, limits) {
  const value =
    typeof input === 'string' || input instanceof Uint8Array ? parseCanonicalJson(input) : input;
  canonicalJson(value, limits);
  return schema.parse(value);
}
// A Dataset page (an App collection read) may fill one frame/host bridge message.
export const DATASET_LIMITS = Object.freeze({ maxBytes: APP_BRIDGE.bytes });
export const parseDataset = (input) => parse(datasetSchema, input, DATASET_LIMITS);
export const parsePresentValue = (input) => parse(presentValue, input);
export const parseColumn = (input) => parse(column, input);
// `limits` bounds the whole input, columns included (default: canonicalJson's).
// A stored row is bounded where it is stored; its readers pass APP_ROW.readBytes.
export const parseEventCells = (input, limits) => parse(eventCellsSchema, input, limits).cells;
export const parseSeries = (input) => parse(seriesSchema, input);
export const parseObservation = (input) => parse(observationSchema, input);
export const parseSnapshot = (input) => parse(snapshotSchema, input);
